import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { MultimodalLiveClient } from '@/lib/multimodal-live-client';
import { GeminiAudioManager } from '@/lib/gemini-audio';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureCanvasViewport, captureScreenShare } from '@/lib/vision-capture';
import { createYouTubeContext, formatYouTubeContextMessage } from '@/lib/youtube-context';
import { devLogger } from '@/lib/dev-logger';

export type AgentState = 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';

type SetMessages = (updater: (prev: any[]) => any[]) => void;

type SlideContextPayload = {
  slide: {
    id?: string;
    title?: string | null;
    slide_type?: string | null;
    section?: string | null;
    tutor_prompt?: string | null;
    duration_minutes?: number | null;
    slide_order?: number | null;
    slideOrder?: number | null;
    completed?: boolean;
  } | null;
  lessonTitle?: string;
  progress?: {
    total?: number;
    completed?: number;
    pendingTitles?: string[];
  };
};

type UseMultimodalLiveProps = {
  selectedDocument?: string | null;
  activeSessionId?: string | null;
  topicId?: string | null;
  setMessages?: SetMessages;
  setUnsavedMessageCount?: (updater: (prev: number) => number) => void;
  appendMessage?: (message: any) => void;
  messageSeqRef?: React.MutableRefObject<number>;
  onAgentActivityChange?: (isActive: boolean) => void;
  getCanvasEl?: (() => HTMLElement | null) | null;
  screenStream?: MediaStream | null;
  getYouTubePlayer?: (() => any) | null;
  currentYouTubeVideoId?: string | null;
  slideContext?: SlideContextPayload | null;
};

export function useMultimodalLive({
  selectedDocument,
  activeSessionId,
  topicId,
  setMessages,
  setUnsavedMessageCount,
  appendMessage,
  messageSeqRef,
  onAgentActivityChange,
  getCanvasEl,
  screenStream,
  getYouTubePlayer,
  currentYouTubeVideoId,
  slideContext,
}: UseMultimodalLiveProps = {}) {
  const { user } = useAuth();
  const [agentStatus, setAgentStatus] = useState<AgentState>('disconnected');
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [sessionDuration, setSessionDuration] = useState('00:00');
  const [isUserMuted, setIsUserMuted] = useState(true);
  const [isPushToTalkLocked, setIsPushToTalkLocked] = useState(false);
  const [isUserSpeakingVad, setIsUserSpeakingVad] = useState(false);

  const [client, setClient] = useState<MultimodalLiveClient | null>(null);
  const clientRef = useRef<MultimodalLiveClient | null>(null);
  const [audioManager, setAudioManager] = useState<GeminiAudioManager | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const hasEverConnectedRef = useRef<boolean>(false);
  const streamingMsgIdRef = useRef<string | null>(null);
  const streamingTextRef = useRef<string>("");
  const userStreamingMsgIdRef = useRef<string | null>(null);
  const userStreamingTextRef = useRef<string>("");
  const mutedRef = useRef<boolean>(true);
  useEffect(() => { mutedRef.current = isUserMuted; }, [isUserMuted]);
  const lastFrameSentRef = useRef<number>(0);
  const isCaptureInProgressRef = useRef<boolean>(false);
  const prevSpeakingRef = useRef<boolean>(false);
  const lastYTKeyRef = useRef<string | null>(null);
  const lastYTTimeRef = useRef<number>(0);
  const backendReadyRef = useRef<boolean>(false);
  const slideContextRef = useRef<SlideContextPayload | null>(slideContext ?? null);
  const lastSlideContextKeyRef = useRef<string | null>(null);
  const lastSlideNudgeKeyRef = useRef<string | null>(null);
  const lastLessonOverviewKeyRef = useRef<string | null>(null);

  const sendSlideContext = useCallback((context: SlideContextPayload | null, force: boolean = false) => {
    const targetClient = clientRef.current;
    if (!targetClient) return;
    if (!backendReadyRef.current) return;
    const progressKey = context?.progress
      ? `${context.progress.completed ?? 'NA'}/${context.progress.total ?? 'NA'}`
      : '';
    const key = context?.slide
      ? `${context.slide.id ?? 'unknown'}:${context.slide.tutor_prompt ?? ''}:${progressKey}`
      : `NO_SLIDE:${progressKey}`;
    if (!force && key === lastSlideContextKeyRef.current) {
      return;
    }
    const payload: Record<string, unknown> = {
      contextType: 'slide',
      lessonTitle: context?.lessonTitle,
      slide: context?.slide
        ? {
            id: context.slide.id,
            title: context.slide.title,
            slideType: context.slide.slide_type,
            section: context.slide.section,
            tutorPrompt: context.slide.tutor_prompt,
            durationMinutes: context.slide.duration_minutes,
          }
        : null,
      cleared: !context?.slide,
    };
    if (context?.progress) {
      payload.progress = {
        total: context.progress.total,
        completed: context.progress.completed,
        pendingTitles: context.progress.pendingTitles,
      };
    }
    try {
      targetClient.sendContextUpdate(payload);
      lastSlideContextKeyRef.current = key;
      devLogger.log('🧭 Sent slide context update', payload);
    } catch (error) {
      devLogger.warn('sendSlideContext failed', error);
    }
  }, []);

  const sendSlideKickoff = useCallback((context: SlideContextPayload | null) => {
    if (!context?.slide) {
      lastSlideNudgeKeyRef.current = null;
      return;
    }
    const targetClient = clientRef.current;
    if (!targetClient || !backendReadyRef.current) return;
    const key = context.slide.id || context.slide.title || JSON.stringify(context.slide);
    if (lastSlideNudgeKeyRef.current === key) return;

    const rawType =
      (context.slide as any).slide_type ||
      (context.slide as any).slideType ||
      "";
    const type = String(rawType).toLowerCase();
    const title = context.slide.title || context.slide.id || "this slide";
    const lessonLabel = context.lessonTitle
      ? ` in "${context.lessonTitle}"`
      : "";

    const total = context.progress?.total;
    const completed = context.progress?.completed ?? 0;
    const remainingTitles = context.progress?.pendingTitles ?? [];
    const slideOrder = context.slide.slide_order ?? context.slide.slideOrder ?? null;
    const slideIndexPhrase = total && slideOrder ? `Slide ${slideOrder} of ${total}` : slideOrder ? `Slide ${slideOrder}` : 'this slide';

    let prompt = `Slide ${slideIndexPhrase}: "${title}"${lessonLabel}.`;
    if (
      type.includes("assessment") ||
      type.includes("practice") ||
      type.includes("quiz") ||
      type.includes("faq") ||
      type.includes("question")
    ) {
      prompt += ` Let me try it first, then coach with hints if I stall and celebrate the key takeaway when I get it.`;
    } else if (type.includes("learn") || type.includes("intro")) {
      prompt += ` Offer either a quick walkthrough or stay on standby while I skim—ask which I prefer before diving in.`;
    }
    if (total && total > 0) {
      const remaining = Math.max(total - completed, 0);
      if (remaining > 0 && remainingTitles.length) {
        const preview = remainingTitles.slice(0, 2).join(' then ');
        prompt += ` After this we'll hit ${preview}.`;
      } else if (remaining === 0) {
        prompt += ` This finishes the lesson, so let's lock it in.`;
      }
    }

    try {
      targetClient.send([{ text: prompt }], true);
      lastSlideNudgeKeyRef.current = key;
      devLogger.log('🎯 Sent slide kickoff prompt', { prompt, slideId: context.slide.id });
    } catch (error) {
      devLogger.warn('sendSlideKickoff failed', error);
    }
  }, []);

  const sendLessonOverview = useCallback((context: SlideContextPayload | null) => {
    if (!context?.progress || !context.lessonTitle) {
      if (!context?.progress) {
        lastLessonOverviewKeyRef.current = null;
      }
      return;
    }
    if (context.slide) {
      return;
    }
    const total = context.progress.total ?? 0;
    if (!total) return;
    const completed = context.progress.completed ?? 0;
    const remainingTitles = context.progress.pendingTitles ?? [];
    const key = `${context.lessonTitle}:${completed}/${total}`;
    if (lastLessonOverviewKeyRef.current === key) {
      return;
    }
    const targetClient = clientRef.current;
    if (!targetClient || !backendReadyRef.current) return;

    const remaining = Math.max(total - completed, 0);
    const nextUp = remainingTitles.length ? remainingTitles[0] : undefined;
    let prompt = `Back at "${context.lessonTitle}" — ${completed}/${total} slides done.`;
    if (remaining > 0 && nextUp) {
      prompt += ` Next up is "${nextUp}". Continue there or revisit something else?`;
    } else if (remaining > 0) {
      prompt += ` ${remaining} slides remain. Want to pick the next one or review an earlier slide?`;
    } else {
      prompt += ` Everything's wrapped. Fancy a recap or a stretch activity?`;
    }

    try {
      targetClient.send([{ text: prompt }], true);
      lastLessonOverviewKeyRef.current = key;
      devLogger.log('📚 Sent lesson overview prompt', { prompt, lessonTitle: context.lessonTitle });
    } catch (error) {
      devLogger.warn('sendLessonOverview failed', error);
    }
  }, []);

  useEffect(() => { clientRef.current = client; }, [client]);

  // timer
  useEffect(() => {
    let t: NodeJS.Timeout | null = null;
    if (isAgentActive && sessionStartRef.current) {
      t = setInterval(() => {
        const elapsed = Date.now() - sessionStartRef.current!;
        const mm = Math.floor(elapsed / 60000).toString().padStart(2, '0');
        const ss = Math.floor((elapsed % 60000) / 1000).toString().padStart(2, '0');
        setSessionDuration(`${mm}:${ss}`);
      }, 1000);
    }
    return () => { if (t) clearInterval(t); };
  }, [isAgentActive]);

const handleConnectAgent = useCallback(async (mode?: string) => {
    if (agentStatus === 'connecting' || agentStatus === 'connected') return;

    // Validate credits before attempting connection
    const { data: userCredits } = await supabase
      .from('user_credits')
      .select('credits_left')
      .eq('user_id', user?.id)
      .single();

    if (!userCredits?.credits_left || userCredits.credits_left <= 0) {
      console.log('❌ No credits available for voice connection');
      try {
        window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog'));
      } catch (error) {
        console.error('Failed to show out of credits dialog:', error);
      }
      return;
    }

    try {
      setAgentStatus('connecting');
      Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'connect.start' });
      const audioMgr = new GeminiAudioManager(null);
      setAudioManager(audioMgr);

      const liveClient = new MultimodalLiveClient({ userId: user?.id });
      // wire events
      liveClient
        .on('open', async () => {
          try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'ws.open' }); } catch {}
          // Ensure playback context is ready so streamed audio can play
          try { await audioMgr.ensurePlaybackContext(); } catch {}
          // Authenticate and send metadata ASAP after open
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            if (token) {
              try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'auth.sent' }); } catch {}
              liveClient.authenticate(token);
            }
            liveClient.sendMetadata({
              sessionId: activeSessionId || undefined,
              topicId: topicId || undefined,
              documentId: selectedDocument || undefined,
              mode: mode || undefined,
            });
            try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'metadata.sent' }); } catch {}
          } catch (e) {
            devLogger.warn('Live auth/metadata send failed', e);
            Sentry.captureException(e as any, { tags: { phase: 'auth_metadata' } });
          }
          // Start mic capture and route frames to Live API when unmuted
          try {
            await audioMgr.startAudioCapture();
            const toB64 = (buf: ArrayBuffer) => {
              const bytes = new Uint8Array(buf);
              let binary = '';
              const step = 0x8000;
              for (let i = 0; i < bytes.length; i += step) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + step)) as unknown as number[]);
              }
              return btoa(binary);
            };
            // Always forward frames; soft-mute is handled in the audio worklet (sends silence)
            audioMgr.setFrameSink((buf: ArrayBuffer) => {
              try {
                const ws = (liveClient as any).ws as WebSocket | null;
                if (!ws || ws.readyState !== WebSocket.OPEN || !backendReadyRef.current) return;
                const b64 = toB64(buf);
                liveClient.sendRealtimeInput([{ mimeType: 'audio/pcm;rate=16000', data: b64 } as any]);
              } catch {}
            });
            // Start muted by default (push-to-talk UX); still sends silence to keep session smooth
            try { await audioMgr.mute(); } catch {}
          } catch (e) {
            devLogger.warn('Mic capture initialization failed', e);
            Sentry.captureException(e as any, { tags: { phase: 'mic_capture' } });
          }
          // Defer marking as connected until backend is ready
        })
        .on('status', (msg: string) => {
          if (msg === 'auth_ok') {
            try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'auth.ok' }); } catch {}
            setAgentStatus('authenticating');
          }
          if (msg === 'auth_failed') {
            try { Sentry.captureMessage('live.auth_failed', { level: 'error' }); } catch {}
            setAgentStatus('error');
          }
          if (msg === 'payload_too_large') {
            devLogger.warn('⚠️ Dropped oversized vision frame by server');
          }
          if (typeof msg === 'string' && msg.toLowerCase().includes('backend is ready')) {
            backendReadyRef.current = true;
            setAgentStatus('connected');
            setIsAgentActive(true);
            hasEverConnectedRef.current = true;
            sessionStartRef.current = Date.now();
            onAgentActivityChange?.(true);
          }
          if (msg === 'out_of_credits') {
            // Stop gracefully; then show out-of-credits dialog
            try { liveClient.disconnect(); } catch {}
            try { audioMgr.setFrameSink(null); } catch {}
            setAgentStatus('error');
            setIsAgentActive(false);
            sessionStartRef.current = null;
            onAgentActivityChange?.(false);
            try { Sentry.captureMessage('live.out_of_credits', { level: 'warning' }); } catch {}
            try { window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog', { detail: { source: hasEverConnectedRef.current ? 'session' : 'preflight' } })); } catch {}
          }
        })
        .on('close', (ev) => {
          try {
            const ce = ev as CloseEvent;
            Sentry.addBreadcrumb({
              category: 'live',
              level: 'info',
              message: `ws.close code=${ce?.code ?? 'n/a'} reason=${ce?.reason ?? ''}`,
            });
            if (ce && ce.code && ce.code !== 1000) {
              Sentry.captureMessage(`live.disconnect code=${ce.code}`, { level: 'warning' });
            }
          } catch {}
          try { audioMgr.setFrameSink(null); } catch {}
          setAgentStatus('disconnected');
          setIsAgentActive(false);
          sessionStartRef.current = null;
        onAgentActivityChange?.(false);
        backendReadyRef.current = false;
          lastSlideContextKeyRef.current = null;
          lastSlideNudgeKeyRef.current = null;
          lastLessonOverviewKeyRef.current = null;
        })
        .on('setupcomplete', () => {
          backendReadyRef.current = true;
          setAgentStatus('connected');
          setIsAgentActive(true);
          hasEverConnectedRef.current = true;
          sessionStartRef.current = Date.now();
          onAgentActivityChange?.(true);
          const context = slideContextRef.current;
          if (context) {
            sendSlideContext(context, true);
            if (context.slide) {
              sendSlideKickoff(context);
            } else {
              sendLessonOverview(context);
            }
          }
        })
        .on('audio', (buf) => { audioMgr.playAudio(buf); })
        .on('inputTranscription', (t) => {
          const incoming = (t.inputTranscription.text || '').trim();
          if (!incoming || !setMessages) return;
          const ensureId = () => {
            if (!userStreamingMsgIdRef.current) {
              userStreamingMsgIdRef.current = `${Date.now()}_${messageSeqRef?.current ?? 0}_user`;
            }
            return userStreamingMsgIdRef.current!;
          };
          const prev = userStreamingTextRef.current || '';
          let nextText: string;
          if (incoming.startsWith(prev)) nextText = incoming;
          else if (prev && prev.startsWith(incoming)) nextText = prev;
          else if (incoming.includes(prev)) nextText = incoming;
          else if (prev && prev.includes(incoming)) nextText = prev;
          else nextText = (prev ? prev + ' ' : '') + incoming;
          userStreamingTextRef.current = nextText;
          const id = ensureId();
          setMessages((prevMsgs: any[]) => {
            const idx = prevMsgs.findIndex(m => m.id === id);
            if (idx === -1) {
              return [
                ...prevMsgs,
                { id, type: 'user', content: nextText, timestamp: new Date(), streaming: true },
              ];
            }
            const copy = [...prevMsgs];
            copy[idx] = { ...copy[idx], content: nextText };
            return copy;
          });
        })
        .on('outputTranscription', (t) => {
          const incoming = (t.outputTranscription.text || '').trim();
          if (!incoming) return;
          setIsAgentTyping(true);

          if (setMessages) {
            const ensureMessageId = () => {
              if (!streamingMsgIdRef.current) {
                streamingMsgIdRef.current = `${Date.now()}_${messageSeqRef?.current ?? 0}_agent`;
              }
              return streamingMsgIdRef.current!;
            };

            // Merge incoming chunk with previously seen text to avoid truncation
            const prev = streamingTextRef.current || '';
            let nextText: string;
            if (incoming.startsWith(prev)) {
              nextText = incoming;
            } else if (prev && prev.startsWith(incoming)) {
              // incoming is shorter (rare) – keep prev to avoid flicker
              nextText = prev;
            } else if (incoming.includes(prev)) {
              nextText = incoming;
            } else if (prev && prev.includes(incoming)) {
              nextText = prev;
            } else {
              nextText = (prev ? prev + ' ' : '') + incoming;
            }
            streamingTextRef.current = nextText;

            const id = ensureMessageId();
            setMessages((prevMsgs: any[]) => {
              const idx = prevMsgs.findIndex(m => m.id === id);
              if (idx === -1) {
                return [
                  ...prevMsgs,
                  { id, type: 'agent', content: nextText, timestamp: new Date(), streaming: true },
                ];
              }
              const copy = [...prevMsgs];
              copy[idx] = { ...copy[idx], content: nextText };
              return copy;
            });
          } else if (appendMessage) {
            // Fallback path if no setMessages provided
            appendMessage({
              id: `${Date.now()}_${messageSeqRef?.current ?? 0}_agent`,
              type: 'agent' as const,
              content: incoming,
              timestamp: new Date(),
            });
          }
        })
        .on('interrupted', () => {
          // Stop any current playback and clear streaming state to avoid stale typing
          try { audioMgr.stopCurrentAudio(); } catch {}
          setIsAgentTyping(false);
          const agentId = streamingMsgIdRef.current;
          const userId = userStreamingMsgIdRef.current;
          if (setMessages && (agentId || userId)) {
            try {
              setMessages((prev: any[]) => prev.map(m =>
                m.id === agentId || m.id === userId ? { ...m, streaming: false } : m
              ));
            } catch {}
          }
          // Reset streaming refs
          streamingMsgIdRef.current = null;
          streamingTextRef.current = '';
          userStreamingMsgIdRef.current = null;
          userStreamingTextRef.current = '';
          devLogger.log('⏹️ Interrupted: cleared streaming and halted playback');
        })
        .on('turncomplete', () => {
          setIsAgentTyping(false);
          if (setMessages) {
            const agentId = streamingMsgIdRef.current;
            const userId = userStreamingMsgIdRef.current;
            setMessages((prev: any[]) => prev.map(m =>
              m.id === agentId || m.id === userId ? { ...m, streaming: false } : m
            ));
            // Increment unsaved count for each finalized message
            if (setUnsavedMessageCount) {
              let inc = 0;
              if (agentId) inc += 1;
              if (userId) inc += 1;
              if (inc > 0) setUnsavedMessageCount((c) => c + inc);
            }
          }
          // reset streaming refs
          streamingMsgIdRef.current = null;
          streamingTextRef.current = '';
          userStreamingMsgIdRef.current = null;
          userStreamingTextRef.current = '';
        });

      setClient(liveClient);
      await liveClient.connect();
    } catch (e) {
      console.error('Live connect error', e);
      setAgentStatus('error');
      setIsAgentActive(false);
      onAgentActivityChange?.(false);
    }
  }, [
    agentStatus,
    appendMessage,
    messageSeqRef,
    onAgentActivityChange,
    sendSlideContext,
    sendSlideKickoff,
    sendLessonOverview,
    setMessages,
    user?.id,
  ]);

  const handleStopConversation = useCallback(() => {
    try { audioManager?.stopCurrentAudio(); } catch {}
    try { audioManager?.setFrameSink(null); } catch {}
    try { client?.disconnect(); } catch {}
    try { audioManager?.stop(); } catch {}
    setClient(null);
    setAudioManager(null);
    setAgentStatus('disconnected');
    setIsAgentActive(false);
    setIsAgentTyping(false);
    sessionStartRef.current = null;
    onAgentActivityChange?.(false);
    backendReadyRef.current = false;
    lastSlideContextKeyRef.current = null;
    lastSlideNudgeKeyRef.current = null;
    lastLessonOverviewKeyRef.current = null;
  }, [audioManager, client, onAgentActivityChange]);

  const sendText = useCallback((text: string) => {
    if (!client) return;
    client.send({ text }, true);
  }, [client]);

  // cleanup on unmount
  useEffect(() => () => {
    try { client?.disconnect(); } catch {}
  }, [client]);

  // Listen to VAD events from audio worklet to drive frame sending
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { speaking?: boolean };
      const speaking = !!detail?.speaking;
      setIsUserSpeakingVad(speaking);
    };
    window.addEventListener('sb-vad-speaking', handler as EventListener);
    return () => window.removeEventListener('sb-vad-speaking', handler as EventListener);
  }, []);

  const sendYouTubeContext = useCallback(async () => {
    if (!client || !getYouTubePlayer || !currentYouTubeVideoId) return;
    try {
      const player = getYouTubePlayer();
      if (!player) return;
      const currentTime = await player.getCurrentTime();
      const ctx = await createYouTubeContext(currentYouTubeVideoId, currentTime);
      if (!ctx) return;
      // Debounce by 30s buckets
      const bucket = `${ctx.videoId}_${Math.floor(ctx.currentTime/30)}`;
      const now = Date.now();
      if (lastYTKeyRef.current === bucket && now - lastYTTimeRef.current < 10000) {
        return;
      }
      lastYTKeyRef.current = bucket;
      lastYTTimeRef.current = now;
      // Send thumbnail if available
      const resp = await fetch(ctx.thumbnail);
      if (resp.ok) {
        const blob = await resp.blob();
        const reader = new FileReader();
        const b64 = await new Promise<string>((res) => { reader.onload = () => res((reader.result as string).split(',')[1]); reader.readAsDataURL(blob); });
        client.sendRealtimeInput([{ mimeType: 'image/jpeg', data: b64 } as any]);
      }
      const msg = formatYouTubeContextMessage(ctx);
      client.send({ text: msg }, true);
    } catch (e) {
      devLogger.warn('sendYouTubeContext failed', e);
    }
  }, [client, getYouTubePlayer, currentYouTubeVideoId]);

  const sendFrame = useCallback(async () => {
    if (!client) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const now = Date.now();
    if (isCaptureInProgressRef.current) return;
    if (now - lastFrameSentRef.current < 2000) return; // throttle 2s
    isCaptureInProgressRef.current = true;
    try {
      if (currentYouTubeVideoId && getYouTubePlayer) {
        devLogger.log('📺 Sending YouTube context/frame');
        await sendYouTubeContext();
        lastFrameSentRef.current = now;
        return;
      }
      let b64: string | null = null;
      if (screenStream && screenStream.getVideoTracks().length > 0) {
        devLogger.log('📸 Capturing screen share frame...');
        b64 = await captureScreenShare(screenStream);
      } else if (getCanvasEl) {
        const el = getCanvasEl();
        if (el) {
          devLogger.log('🖼️ Capturing canvas viewport frame...');
          b64 = await captureCanvasViewport(el);
        } else {
          devLogger.warn('⚠️ getCanvasEl returned null');
        }
      }
      if (b64) {
        client.sendRealtimeInput([{ mimeType: 'image/jpeg', data: b64 } as any]);
        lastFrameSentRef.current = now;
        devLogger.log('📤 Vision frame sent (jpeg), chars=', b64.length);
      } else {
        devLogger.warn('⚠️ No frame available to send');
      }
    } catch (e) {
      devLogger.warn('sendFrame failed', e);
    } finally {
      isCaptureInProgressRef.current = false;
    }
  }, [client, getCanvasEl, screenStream, getYouTubePlayer, currentYouTubeVideoId, sendYouTubeContext]);

  // Rising edge: send immediately when speaking starts
  useEffect(() => {
    if (agentStatus !== 'connected') return;
    if (isUserSpeakingVad && !prevSpeakingRef.current) {
      sendFrame();
    }
    prevSpeakingRef.current = isUserSpeakingVad;
  }, [isUserSpeakingVad, agentStatus, sendFrame]);

  // While speaking: heartbeat every 2s
  useEffect(() => {
    if (agentStatus !== 'connected' || !isUserSpeakingVad) return;
    const t = setInterval(() => { sendFrame(); }, 2000);
    return () => clearInterval(t);
  }, [isUserSpeakingVad, agentStatus, sendFrame]);

  // Initial frame on connect
  useEffect(() => {
    if (agentStatus === 'connected') {
      sendFrame();
    }
  }, [agentStatus, sendFrame]);

  // Slide context updates
  useEffect(() => {
    slideContextRef.current = slideContext ?? null;
    if (agentStatus !== 'connected' || !backendReadyRef.current) {
      return;
    }
    sendSlideContext(slideContext ?? null);
    if (slideContext?.slide) {
      sendSlideKickoff(slideContext);
    } else {
      sendLessonOverview(slideContext ?? null);
    }
  }, [agentStatus, slideContext, sendLessonOverview, sendSlideContext, sendSlideKickoff]);

  // Push-to-talk handlers (defined before keyboard listeners to avoid TDZ)
  const handlePushToTalkStart = useCallback(() => {
    setIsUserMuted(false);
    try { audioManager?.unmute(); } catch {}
    // Kick off a frame immediately when user starts talking
    try { sendFrame(); } catch {}
  }, [sendFrame, audioManager]);

  const handlePushToTalkEnd = useCallback(() => {
    if (!isPushToTalkLocked) {
      setIsUserMuted(true);
      try { audioManager?.mute(); } catch {}
    }
  }, [isPushToTalkLocked, audioManager]);

  // Keyboard push-to-talk (spacebar)
  const keyHoldRef = useRef(false);
  const isTextInput = (el: EventTarget | null) => {
    const node = el as HTMLElement | null;
    if (!node) return false;
    const tag = (node.tagName || '').toLowerCase();
    const editable = (node as HTMLElement).isContentEditable;
    return tag === 'input' || tag === 'textarea' || editable;
  };
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code !== 'Space') return;
    if (isTextInput(event.target)) return; // don’t steal typing
    if (!(agentStatus === 'connected' && !isPushToTalkLocked)) return;
    if (keyHoldRef.current) return; // already active
    keyHoldRef.current = true;
    event.preventDefault();
    try { handlePushToTalkStart(); } catch {}
  }, [agentStatus, isPushToTalkLocked, handlePushToTalkStart]);
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.code !== 'Space') return;
    if (!keyHoldRef.current) return;
    keyHoldRef.current = false;
    event.preventDefault();
    try { handlePushToTalkEnd(); } catch {}
  }, [handlePushToTalkEnd]);

  useEffect(() => {
    if (!(agentStatus === 'connected')) return;
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [agentStatus, handleKeyDown, handleKeyUp]);

  // YouTube auto-unlock push-to-talk (align with legacy)
  useEffect(() => {
    const onYTPlaying = () => {
      if (isPushToTalkLocked && isAgentActive) {
        setIsPushToTalkLocked(false);
        if (!isUserMuted) {
          setIsUserMuted(true);
          try { audioManager?.mute(); } catch {}
        }
      }
    };
    window.addEventListener('youtube-playing', onYTPlaying);
    return () => window.removeEventListener('youtube-playing', onYTPlaying);
  }, [isPushToTalkLocked, isAgentActive, isUserMuted, audioManager]);

  // Suspend frame sending (and soft-mute) when tab hidden; restore on visible
  const wasUnmutedRef = useRef(false);
  useEffect(() => {
    const onVis = () => {
      const hidden = document.hidden;
      if (hidden) {
        wasUnmutedRef.current = !isUserMuted;
        try { audioManager?.mute(); } catch {}
      } else {
        if (wasUnmutedRef.current) {
          try { audioManager?.unmute(); } catch {}
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [audioManager, isUserMuted]);

  // Mic controls
  const toggleMute = useCallback(async () => {
    if (!audioManager) { setIsUserMuted(m => !m); return; }
    setIsUserMuted((prev) => {
      const next = !prev;
      // Soft-mute keeps frames flowing (silence)
      if (next) {
        audioManager.mute().catch(() => {});
      } else {
        audioManager.unmute().catch(() => {});
      }
      return next;
    });
  }, [audioManager]);

  

  const handleToggleLock = useCallback(() => {
    setIsPushToTalkLocked((prevLocked) => {
      const newLocked = !prevLocked;
      // When locking: unmute for audio passthrough
      // When unlocking: mute to return to push-to-talk mode
      const newMuted = !newLocked;
      setIsUserMuted(newMuted);
      
      // Update audio manager state
      if (audioManager) {
        if (newMuted) {
          audioManager.mute().catch(() => {});
        } else {
          audioManager.unmute().catch(() => {});
        }
      }
      
      return newLocked;
    });
  }, [audioManager]);

  return {
    agentStatus,
    isAgentActive,
    isUserMuted,
    isPushToTalkLocked,
    sessionDuration,
    isAgentTyping,
    isUserSpeakingVad,
    handleConnectAgent,
    handleStopConversation,
    toggleMute,
    handlePushToTalkStart,
    handlePushToTalkEnd,
    handleToggleLock,
    sendText,
  };
}
